// Landing for any /series/:slug (or /series/:slug/:id) with no page here. Shared by both routes and deliberately IDENTICAL for every miss: a slug that never existed and a real-but-unpublished show must be indistinguishable, or this page becomes an enumeration oracle over every private series. No slug, no title, no "did you mean".
// Node-prerender-safe: MUI + local helpers only.
import { Link as RouterLink } from 'react-router';
import Container from '@mui/material/Container';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Link from '@mui/material/Link';
import Button from '@mui/material/Button';
import { SplashChrome } from './splash-chrome';
import { ContactEmail, WhatMoved } from './series-migration';

export const NOT_FOUND_META = [
  { title: 'Not published here — Gnothi Series' },
  { name: 'robots', content: 'noindex,follow' },
];

export default function SeriesNotFound() {
  return (
    // SplashChrome wears `Mui` itself — never nest a second ThemeProvider here.
    <SplashChrome active="series">
      <Container maxWidth="sm" sx={{ py: 8 }}>
        <Stack spacing={2}>
          <Typography variant="h5" component="h1">This series isn't published here</Typography>
          {/* Most arrivals here followed a link to a series that really does still exist — it just isn't one of the ones with a public web page. Say that without confirming (or denying) that this particular address is one of them, then let WhatMoved carry the case where it genuinely didn't come across, so nobody is quietly told their show is fine when it isn't. */}
          <Typography color="text.secondary">OCDevel Podcaster is now Gnothi Series, and links kept their address. Only series whose owner publishes them get a page on the web — the rest stay private, or play only through a podcast app — so there's nothing to show at this one.</Typography>
          <Typography color="text.secondary"><WhatMoved /></Typography>
          {/* Deliberately after WhatMoved, and hedged to "feeds that came across": subscribers surviving is the most reassuring true fact here, but stated flat it would also promise it to the cohort whose show didn't come across at all. */}
          <Typography color="text.secondary">If you subscribed to a show in a podcast app, stay subscribed. Feeds that came across came across intact — the old addresses still resolve and new episodes keep arriving, so there's nothing to re-add.</Typography>
          <Typography color="text.secondary">If it's yours, sign in with the same account and you'll find it under Series. Open it and publish it — that's all it takes to put it back on the web. If it isn't there, or it's private and you'd rather it weren't, email <ContactEmail /> — that's me, and I'd rather hear about it.</Typography>
          <Stack direction="row" spacing={2} sx={{ pt: 1 }}>
            <Button component={RouterLink} to="/series" variant="contained">Browse Gnothi Series</Button>
            <Link component={RouterLink} to="/" sx={{ alignSelf: 'center' }}>Sign in</Link>
          </Stack>
        </Stack>
      </Container>
    </SplashChrome>
  );
}
